<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import type { Address } from "@types"
import { formatAddress, formatIdSource } from "@core/helpers"
import { SOURCES_TYPES } from "@core/constants"
import { useI18n } from "vue-i18n"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            address: "Adresse",
            pdl: "compteur électrique",
            pdl_plural: "compteurs électriques",
            pce: "compteur de gaz",
            pce_plural: "compteurs de gaz",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
interface Props {
    /**
     * Nom du bâtiment
     */
    name?: string
    /**
     * Adresse du bâtiment
     */
    address?: Address | null
    /**
     * Liste des identifiants de compteurs électriques (PDL)
     */
    pdl?: string[]
    /**
     * Liste des identifiants de compteurs de gaz (PCE)
     */
    pce?: string[]
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    name: "",
    address: null,
    pdl: () => [],
    pce: () => [],
})

// ================================
// COMPUTED
// ================================
/**
 * Adresse formatée du bâtiment
 */
const getFormattedAddress = computed(() => {
    return props.address ? formatAddress(props.address) : ""
})

/**
 * Liste des PDL formatés
 */
const getFormattedPdl = computed(() => {
    return props.pdl.map((idsource) =>
        formatIdSource(idsource, SOURCES_TYPES.PDL)
    )
})

/**
 * Liste des PCE formatés
 */
const getFormattedPce = computed(() => {
    return props.pce.map((idsource) =>
        formatIdSource(idsource, SOURCES_TYPES.PCE)
    )
})
</script>

<template>
    <div class="item-building-recap flex flex-col gap-4">
        <!-- Nom du bâtiment -->
        <p v-if="name" class="text-sm font-semibold text-gray-700">
            {{ name }}
        </p>

        <!-- Adresse -->
        <div v-if="getFormattedAddress" class="flex flex-col gap-1">
            <h5 class="text-xs font-medium text-gray-500">
                {{ t("address") }}
            </h5>
            <p class="text-sm text-gray-700">{{ getFormattedAddress }}</p>
        </div>

        <!-- Compteurs électriques (PDL) -->
        <div
            v-if="pdl && pdl.length > 0"
            class="flex flex-col gap-1 p-4 border-l-4 border-gray-200 rounded hover:border-cyan-400 transition-colors hover:bg-cyan-50"
        >
            <h5 class="text-xs font-medium text-gray-500">
                {{ pdl.length }}
                {{ pdl.length > 1 ? t("pdl_plural") : t("pdl") }}
            </h5>
            <p class="text-sm text-gray-700">
                {{ getFormattedPdl.join("  •  ") }}
            </p>
        </div>

        <!-- Compteurs de gaz (PCE) -->
        <div
            v-if="pce && pce.length > 0"
            class="flex flex-col gap-1 p-4 border-l-4 border-gray-200 rounded hover:border-cyan-400 transition-colors hover:bg-cyan-50"
        >
            <h5 class="text-xs font-medium text-gray-500">
                {{ pce.length }}
                {{ pce.length > 1 ? t("pce_plural") : t("pce") }}
            </h5>
            <p class="text-sm text-gray-700">
                {{ getFormattedPce.join("  •  ") }}
            </p>
        </div>
    </div>
</template>
